// frontend/src/pages/ForgotPassword.jsx

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "../axiosConfig";

export default function ForgotPassword() {
  const [email, setEmail]   = useState("");
  const [status, setStatus] = useState(null); // null | 'loading' | {type,message}

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailTrim = email.trim();
    if (!emailTrim) return;

    setStatus("loading");
    try {
      await axios.post("/forgot-password", { email: emailTrim });
      setStatus({
        type: "success",
        message:
          "If that email is registered, you'll receive reset instructions.",
      });
    } catch (err) {
      setStatus({
        type: "error",
        message: err.response?.data?.error || "Request failed. Please try again.",
      });
    }
  };

  const isLoading = status === "loading";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-3 sm:px-4 py-6">
      {/* Autofill + dark-mode contrast fix */}
      <style>{`
        .dark input:-webkit-autofill,
        .dark input:-webkit-autofill:focus,
        .dark input:-webkit-autofill:hover {
          -webkit-text-fill-color: #e5e7eb; /* slate-200 */
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #e5e7eb;
        }
        .dark input:-webkit-autofill::first-line { color: #e5e7eb; }
      `}</style>

      <motion.div
        className="w-full max-w-sm sm:max-w-md bg-white dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg p-4 sm:p-6 md:p-8"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <h1 className="text-2xl sm:text-3xl font-bold text-center text-indigo-700 dark:text-indigo-300 mb-2">
          Forgot Password
        </h1>
        <p className="text-center text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-4 sm:mb-6">
          Enter your email to receive a password reset link.
        </p>

        {/* Status banners */}
        {status && status !== "loading" && (
          <div
            className={`text-center text-xs sm:text-sm p-2.5 sm:p-3 mb-4 rounded-md border
              ${
                status.type === "success"
                  ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800/40"
                  : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/40"
              }`}
            role="alert"
            aria-live="polite"
          >
            {status.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
          <div>
            <label className="block text-gray-800 dark:text-gray-200 mb-1 text-xs sm:text-sm">
              Email
            </label>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/80 text-gray-900 dark:text-slate-100 placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 transition text-sm sm:text-base"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full text-white font-semibold py-2.5 rounded-lg transition text-sm sm:text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 dark:focus-visible:ring-indigo-500
              ${
                isLoading
                  ? "bg-indigo-400 dark:bg-indigo-500 cursor-not-allowed"
                  : "bg-indigo-700 hover:bg-indigo-800 dark:bg-indigo-600 dark:hover:bg-indigo-700"
              }`}
          >
            {isLoading ? "Sending…" : "Send Reset Link"}
          </button>

          <div className="text-center text-xs sm:text-sm text-gray-700 dark:text-gray-300">
            Remembered your password?{" "}
            <Link to="/login" className="text-indigo-700 dark:text-indigo-400 hover:underline">
              Back to Login
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
